import React, { useState } from 'react';
import {
  Box,
  ButtonBase,
  Divider,
  Popover,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AppsIcon from '@mui/icons-material/Apps';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import MovieFilterIcon from '@mui/icons-material/MovieFilter';
import CollectionsIcon from '@mui/icons-material/Collections';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import KeyIcon from '@mui/icons-material/Key';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { useLocation, useNavigate } from 'react-router-dom';

import { useLabs } from '../../../queries';
import { hexWithAlpha } from '../../../routes/dashboard/labCapabilities';
import { activeAppId, buildAppSwitcherItems } from './appSwitcherItems';
import type { AppSwitcherItem } from './appSwitcherItems';

const ICONS: Record<string, React.ReactElement> = {
  'image-lab': <ImageIcon />,
  'writer-lab': <DescriptionIcon />,
  'animation-lab': <MovieFilterIcon />,
  assets: <CollectionsIcon />,
  dashboard: <SpaceDashboardIcon />,
  'settings-keys': <KeyIcon />,
};

interface Props {
  /** Current lab, so the grid can mark where you already are. */
  labId: string;
  /** Lab display name shown next to the icon in the trigger. */
  labName: string;
}

/**
 * App switcher in the lab TopBar — the Google apps-grid pattern.
 *
 * The lab identity at the left of the TopBar becomes the trigger: clicking it
 * opens a compact 3-across grid of the labs and top-level surfaces, so you can
 * move between them without going back out to the dashboard or guessing a URL.
 *
 * Labs come from the backend catalog (see `buildAppSwitcherItems`), so the grid
 * respects the SMA-13 feature gate automatically.
 */
export const AppSwitcher: React.FC<Props> = ({ labId, labName }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: labsData } = useLabs();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const open = Boolean(anchorEl);
  const items = buildAppSwitcherItems(labsData?.labs);
  const currentId = activeAppId(location.pathname, labId);

  const labs = items.filter(i => i.group === 'lab');
  const surfaces = items.filter(i => i.group === 'surface');

  const go = (item: AppSwitcherItem) => {
    setAnchorEl(null);
    // Navigating to the app you're already in is a no-op rather than a reload
    // that would drop unsaved canvas/editor state.
    if (item.id !== currentId) navigate(item.to);
  };

  return (
    <>
      <Tooltip title="Switch app">
        <ButtonBase
          onClick={e => setAnchorEl(e.currentTarget)}
          aria-label="Switch app"
          aria-haspopup="true"
          aria-expanded={open}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            minWidth: 0,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <AppsIcon fontSize="small" sx={{ opacity: 0.8 }} />
          <Typography variant="subtitle2" noWrap>
            {labName}
          </Typography>
        </ButtonBase>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, borderRadius: 3, p: 1.5, width: 300 } } }}
      >
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ px: 1, display: 'block' }}
        >
          Labs
        </Typography>
        <AppGrid items={labs} currentId={currentId} onPick={go} />

        <Divider sx={{ my: 1 }} />

        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ px: 1, display: 'block' }}
        >
          Surfaces
        </Typography>
        <AppGrid items={surfaces} currentId={currentId} onPick={go} />
      </Popover>
    </>
  );
};

const AppGrid: React.FC<{
  items: AppSwitcherItem[];
  currentId?: string;
  onPick: (item: AppSwitcherItem) => void;
}> = ({ items, currentId, onPick }) => (
  <Box
    sx={{
      display: 'grid',
      // Three across, like the Google grid — wide enough for two-word labels
      // without truncating "Animation Lab".
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 0.5,
    }}
  >
    {items.map(item => (
      <AppTile
        key={item.id}
        item={item}
        active={item.id === currentId}
        onPick={() => onPick(item)}
      />
    ))}
  </Box>
);

const AppTile: React.FC<{
  item: AppSwitcherItem;
  active: boolean;
  onPick: () => void;
}> = ({ item, active, onPick }) => {
  const theme = useTheme();
  const accent = theme.palette.primary.main;

  return (
    <Tooltip title={item.description ?? ''} placement="bottom">
      <ButtonBase
        onClick={onPick}
        aria-label={item.label}
        aria-current={active ? 'page' : undefined}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          borderRadius: 2,
          py: 1.25,
          px: 0.5,
          width: '100%',
          bgcolor: active ? hexWithAlpha(accent, 0.1) : 'transparent',
          '&:hover': { bgcolor: active ? hexWithAlpha(accent, 0.16) : 'action.hover' },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            color: active ? accent : 'text.secondary',
            bgcolor: active ? hexWithAlpha(accent, 0.16) : 'action.hover',
          }}
        >
          {ICONS[item.id] ?? <ScienceOutlinedIcon />}
        </Box>
        <Typography
          variant="caption"
          align="center"
          sx={{
            lineHeight: 1.2,
            fontWeight: active ? 600 : 400,
            color: active ? 'text.primary' : 'text.secondary',
          }}
        >
          {item.label}
        </Typography>
      </ButtonBase>
    </Tooltip>
  );
};
